import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

export default function CustomLegend({
  barProps,
  setBarProps,
  keys,
  improvement,
  w,
  additionalLegend,
  ...props
}: any) {
  const showReset = !keys?.every((item: any) => barProps[item.key]);
  const [hasShownTooltip, setHasShownTooltip] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const decreaseKeys = ['clusterP5ToP10Percentile', 'clusterP0ToP5Percentile'];
  const increaseKeys = [
    'clusterP90ToP95Percentile',
    'clusterP95ToP100Percentile',
  ];

  useEffect(() => {
    if (!improvement) return;

    setBarProps(
      keys?.reduce(
        (a: any, { key }: any) => {
          if (improvement === 'INCREASED') {
            a[key] = !increaseKeys.includes(key);
          } else if (improvement === 'DECREASED') {
            a[key] = !decreaseKeys.includes(key);
          } else {
            a[key] = true;
          }
          return a;
        },
        { hover: null }
      )
    );
  }, [improvement, keys]);

  const resetSelected = () => {
    setBarProps(
      keys?.reduce(
        (a: any, { key }: any) => {
          a[key] = true;
          return a;
        },
        {
          hover: null,
        }
      )
    );
  };

  return (
    <Box
      padding={'0 20px'}
      w={w ? w : 220}
      height="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Tooltip
        label={'Double-click on legend to isolate a single metric.'}
        isOpen={showTooltip}
        placement="top"
        w={170}
        fontSize={'xs'}
      >
        <Text
          fontWeight={600}
          fontSize={'xs'}
          textDecor={'underline'}
          w={'fit-content'}
          mb={1}
          onClick={() => {
            if (!showReset) {
              return;
            }
            resetSelected();
          }}
          visibility={showReset ? 'visible' : 'hidden'}
          cursor={'pointer'}
          flexShrink={0}
        >
          Reset
        </Text>
      </Tooltip>
      <Box
        flex="1"
        minH={0}
        overflowY={'auto'}
        overflowX={'auto'}
        css={{
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgb(245, 245, 245)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgb(184, 183, 183)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgb(183, 182, 182)',
          },
        }}
      >
        {keys?.map((entry: any) => {
          const isLine = ['line_chart', 'dashed_line_chart'].includes(
            entry?.chartType
          );
          return (
            <Flex
              key={entry?.key}
              align={'center'}
              w={'fit-content'}
              cursor={'pointer'}
              onDoubleClick={() => {
                setBarProps(
                  keys?.reduce(
                    (a: any, { key }: any) => {
                      a[key] = entry?.key === key ? true : false;
                      return a;
                    },
                    {
                      hover: null,
                    }
                  )
                );
              }}
              onClick={() => {
                if (!hasShownTooltip) {
                  setHasShownTooltip(true);
                  setShowTooltip(true);
                  setTimeout(() => {
                    setShowTooltip(false);
                  }, 1500);
                }

                setBarProps((barProps: any) => {
                  return {
                    ...barProps,
                    [entry?.key]: !barProps[entry?.key],
                    hover: null,
                  };
                });
              }}
              onMouseEnter={() => {
                if (!barProps[entry?.key]) {
                  return;
                }
                setBarProps((barProps: any) => {
                  return { ...barProps, hover: entry.key };
                });
              }}
              onMouseLeave={() => {
                setBarProps((barProps: any) => {
                  return { ...barProps, hover: null };
                });
              }}
            >
              {isLine ? (
                <svg width="20" height="3" style={{ marginRight: '5px' }}>
                  <line
                    x1="0"
                    y1="1.5"
                    x2="20"
                    y2="1.5"
                    stroke={barProps[entry?.key] ? entry?.color : 'grey'}
                    strokeWidth="3"
                    strokeDasharray={
                      entry?.chartType === 'dashed_line_chart' ? '2 2' : 'none'
                    }
                  />
                </svg>
              ) : (
                <svg width="20" height="20" style={{ marginRight: '5px' }}>
                  <circle
                    cx="10"
                    cy="10"
                    r="5"
                    fill={barProps[entry?.key] ? entry?.color : 'grey'}
                    fillOpacity={
                      entry?.chartType === 'dashed_bar_chart' ? 0.2 : 1
                    }
                    stroke={
                      entry?.chartType === 'dashed_bar_chart'
                        ? entry?.color
                        : 'none'
                    }
                    strokeDasharray={
                      entry?.chartType === 'dashed_bar_chart' ? '2 2' : 'none'
                    }
                  />
                </svg>
              )}
              <Text
                _hover={
                  barProps[entry?.key]
                    ? { color: entry?.color, fontWeight: 600 }
                    : { color: 'grey' }
                }
                color={barProps[entry?.key] ? entry?.color : 'grey'}
                decoration={barProps[entry?.key] ? 'auto' : 'line-through'}
              >
                {entry?.name}
              </Text>
            </Flex>
          );
        })}

        {additionalLegend?.map((legend: any) => (
          <Flex align="center" key={legend.name || legend.color}>
            <svg width="20" height="3" style={{ marginRight: '5px' }}>
              <line
                x1="0"
                y1="1.5"
                x2="20"
                y2="1.5"
                stroke={legend.color}
                strokeWidth="3"
                strokeDasharray={legend.lineType === 'dotted' ? '2 2' : ''}
              />
            </svg>
            <Text color={legend.color}>{legend.name}</Text>
          </Flex>
        ))}
      </Box>
    </Box>
  );
}
